import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { TmsWebBaseEditCreateService } from '../base';
import {
  ApiResult,
  PickingListLabel,
  QuickDespatchResponse,
  SalesOrderStockAllocation,
  SalesOrderStockAllocationBulkSave,
  SalesOrderStockAllocationBulkSaveResult,
  StockItemAllocation,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class SalesOrderStockAllocationService extends TmsWebBaseEditCreateService<
  SalesOrderStockAllocation,
  SalesOrderStockAllocation
> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('SalesOrderStockAllocation', $localize`Sales Order Stock Allocation`);
  }

  allocate(
    salesOrderLineId: string,
    stockItemId: string,
    hardAllocation: boolean,
    printPickingList: boolean,
  ): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/Allocate`, {
      salesOrderLineId,
      stockItemId,
      hardAllocation,
      printPickingList,
    });
  }

  swapStockAllocations(stockNo: string, selectedOrders: StockItemAllocation[]): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/SwapStockAllocations`, {
      stockNo,
      selectedOrders,
    });
  }

  updateSalesOrderStockAllocation(salesOrderStockAllocationId: string, quantity: number, focQuantity: boolean) {
    return this.httpService.post<any>(`${this.apiUrl}/UpdateSalesOrderStockAllocation`, {
      salesOrderStockAllocationId,
      quantity,
      focQuantity,
    });
  }

  updateHardAllocation(salesOrderStockAllocationId: string, hardAllocation: boolean) {
    return this.httpService.post<any>(`${this.apiUrl}/UpdateHardAllocation`, {
      salesOrderStockAllocationId,
      hardAllocation,
    });
  }

  getAllocations(salesOrderLineId: any, picking: boolean): Observable<SalesOrderStockAllocation[]> {
    return this.httpService.get<SalesOrderStockAllocation[]>(
      `${this.apiUrl}/GetAllocations?salesOrderLineId=${salesOrderLineId}&picking=${picking}`,
    );
  }

  getPickingListLabel(salesOrderStockAllocationId: any, previousLocation: boolean): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(
      `${this.apiUrl}/GetPickingListLabel?salesOrderStockAllocationId=${salesOrderStockAllocationId}&previousLocation=${previousLocation}`,
    );
  }

  getPickingListLabelByDespatchNoteId(salesDespatchNoteId: string): Observable<PickingListLabel[]> {
    return this.httpService.get<PickingListLabel[]>(
      `${this.apiUrl}/GetPickingListLabelByDespatchNoteId?salesDespatchNoteId=${salesDespatchNoteId}`,
    );
  }

  // overrideOnStop: the user said Yes to the on-stop prompt (an OnStop-coded refusal), so the
  // API records a Picking override and picks. Sent false the first time.
  pick(salesOrderStockAllocationId: string, overrideOnStop = false) {
    return this.httpService.post<PickingListLabel>(`${this.apiUrl}/Pick`, {
      salesOrderStockAllocationId,
      overrideOnStop,
    });
  }

  pickingMultiPiece(
    salesOrderLineId: string,
    stockItemIds: string[],
    stockRefs: string[],
    dimensions: string[],
    quantities: number[],
    grossWgt: number[],
    nettWgt: number[],
    overrideOnStop = false,
    overrideAllocatedElsewhere = false,
  ): Observable<QuickDespatchResponse> {
    return this.httpService.post<QuickDespatchResponse>(`${this.apiUrl}/PickingMultiPiece`, {
      salesOrderLineId,
      stockItemIds,
      stockRefs,
      dimensions,
      quantities,
      grossWgt,
      nettWgt,
      overrideOnStop,
      overrideAllocatedElsewhere,
    });
  }

  saveAllocations(salesOrderLineId: string, selected: boolean[], stockItemIds: string[], quantities: number[]) {
    return this.httpService.post(`${this.apiUrl}/SaveAllocations`, {
      salesOrderLineId,
      selected,
      stockItemIds,
      quantities,
    });
  }

  /**
   * Bulk mode on the allocations screen: every tick, un-tick and quantity edit the user made
   * goes up in ONE request, all-or-nothing. A 400 body names the roll that was refused.
   */
  saveBulkAllocations(model: SalesOrderStockAllocationBulkSave): Observable<SalesOrderStockAllocationBulkSaveResult> {
    return this.httpService.post<SalesOrderStockAllocationBulkSaveResult>(`${this.apiUrl}/SaveBulkAllocations`, model);
  }

  /**
   * `data` is the picked allocations (SalesOrderStockAllocation[]) to print from; `cautions` names the
   * picks refused because their allocation had gone, while the rest were picked (E-4, Oct 2026).
   * The body is empty when no ids were sent.
   */
  multiPick(ids: string[], overrideOnStop = false): Observable<ApiResult> {
    return this.httpService.post<ApiResult>(`${this.apiUrl}/MultiPick`, {
      ids,
      overrideOnStop,
    });
  }

  unpick(salesOrderStockAllocationId: string, mergeStock: boolean) {
    return this.httpService.post<any>(`${this.apiUrl}/Unpick`, {
      salesOrderStockAllocationId,
      mergeStock,
    });
  }

  updatePicked(salesOrderStockAllocationId: string, nettQuantity: number) {
    return this.httpService.post<PickingListLabel>(`${this.apiUrl}/UpdatePicked`, {
      salesOrderStockAllocationId,
      nettQuantity,
    });
  }

  // Corrects what a picked line charges; the rest of what was sent becomes free of charge.
  updateCharged(salesOrderStockAllocationId: string, charged: number): Observable<void> {
    return this.httpService.post<void>(`${this.apiUrl}/UpdateCharged`, {
      salesOrderStockAllocationId,
      charged,
    });
  }

  pickDirectDespatchLine(
    salesOrderLineId: string,
    quantity: number,
    overrideOnStop = false,
  ): Observable<QuickDespatchResponse> {
    return this.httpService.post<QuickDespatchResponse>(`${this.apiUrl}/PickDirectDespatchLine`, {
      salesOrderLineId,
      quantity,
      overrideOnStop,
    });
  }

  /** `data` is the ids of the despatch notes created (string[]); `quickDespatch` the quick despatch outcomes. */
  pickDirectDespatchLines(
    lines: { salesOrderLineId: string; quantity: number }[],
    overrideOnStop = false,
  ): Observable<ApiResult> {
    return this.httpService.post<ApiResult>(`${this.apiUrl}/PickDirectDespatchLines`, { lines, overrideOnStop });
  }
}
